import { Box } from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { DataSet } from "vis-data"
import {
  DataGroup,
  DataItem,
  Graph2d,
  Graph2dOptions,
  TimelineEventPropertiesResult,
  TimelineTimeAxisOption,
} from "vis-timeline"
import * as graph2d from "@hydrologiq/common/types/graph/timeseries"
import {
  dateFromNormalDate,
  dateFromTimestamp,
  nowDateTime,
  toUserTZ,
  normalDate,
} from "@hydrologiq/common/helpers/date"
import { Scale } from "@hydrologiq/schemas/1.0.0/installationPage"
import { toKey } from "@hydrologiq/common/test/toKey"
import "./TimeSeries.scss"
import "vis-timeline/dist/vis-timeline-graph2d.min.css"
import { DateTime, seconds } from "timezonecomplete"

const WEEK_SECONDS = 60 * 60 * 24 * 7
const TWO_WEEK_SECONDS = WEEK_SECONDS * 2
const HOUR_SECONDS = 60 * 1
const TWO_HOUR_SECONDS = HOUR_SECONDS * 2

export type DataPoint = {
  pageX: number
  pageY: number
  time: DateTime
  value: number
  group: string
}

export interface TimeSeriesProps {
  groups: graph2d.TimeGroup[]
  valueAxis: graph2d.Axis
  startResolutionS?: number
  endResolutionS?: number
  lineDate?: DateTime
  shaded?: boolean
  timeAxisScale?: Scale
  graphId?: string
  minYValue?: number
  intervalMS?: number
  onDataPoint?: (points: DataPoint[]) => void
  onLoaded?: () => void
  onMouseLeave?: () => void
}

type Graph2dOptionsExtended = {
  interpolation: boolean
  shaded: boolean
} & Graph2dOptions

type DataItemExtended = {
  x: Date
  y: number
} & DataItem

const mapPoint = (point: graph2d.TimePoint, index: number, graphId: string | undefined) => {
  return {
    x: point.time.toIsoString(),
    y: point.value,
    group: index.toString(),
    label: {
      content: point.value,
      className: toKey(`point-${graphId}-${point.time.unixUtcMillis()}-${index}`),
    },
  }
}

function TimeSeries({
  groups,
  valueAxis,
  startResolutionS = TWO_WEEK_SECONDS,
  endResolutionS = TWO_HOUR_SECONDS,
  lineDate,
  shaded = true,
  timeAxisScale,
  graphId,
  minYValue,
  intervalMS,
  onDataPoint,
  onLoaded,
  onMouseLeave,
}: TimeSeriesProps) {
  const visJsRef = useRef<HTMLDivElement>(null)
  const [timeline, setTimeline] = useState<Graph2d | null>(null)

  const [loaded, setLoaded] = useState<boolean>(false)
  const [items, setItems] = useState<DataSet<DataItemExtended> | undefined>(undefined)
  const [options, setOptions] = useState<Graph2dOptionsExtended | undefined>(undefined)
  const [graphGroups, setGraphGroups] = useState<DataSet<DataGroup> | undefined>(undefined)

  const fullscreen = window.innerWidth >= 960

  const getOptions = (minValue: number | undefined, maxValue: number | undefined, endDate: DateTime | undefined) => {
    const options: Graph2dOptionsExtended = {
      drawPoints: true,
      clickToUse: false,
      zoomMax: 1000000000000000,
      interpolation: false,
      showCurrentTime: true,
      moveable: false,
      shaded: shaded,
      legend: {
        enabled: true,
        left: {
          visible: true,
          position: "top-right",
        },
      },
    }

    if (startResolutionS > TWO_WEEK_SECONDS * 7)
      options["format"] = {
        majorLabels: {
          day: "MMM YY",
        },
      }
    else {
      options["format"] = {
        majorLabels: {
          day: "MMMM YY",
        },
      }
    }

    if (timeAxisScale) {
      let scale: TimelineTimeAxisOption = {
        scale: timeAxisScale.normal.resolution,
        step: timeAxisScale.normal.quantity,
      }
      if (!fullscreen && timeAxisScale.mobile)
        scale = { scale: timeAxisScale.mobile.resolution, step: timeAxisScale.mobile.quantity }
      options["timeAxis"] = scale
    }

    options["dataAxis"] = { left: {} }
    if (fullscreen) options["dataAxis"] = { left: { title: { text: valueAxis.title } } }

    // Override min value
    minValue = 0
    if (minValue !== undefined && maxValue !== undefined) {
      if (maxValue < 5) {
        if (maxValue < 0.1) maxValue = 0.1
        if (minValue === 0) {
          minValue = -1
        }
        if (maxValue < 1) {
          maxValue = 1
        }
        if (minValue > 0) minValue -= minValue * 0.01
      }

      if (minYValue !== undefined) minValue = minYValue

      // Apply a scale to min value if it's between -1 and 0
      // based upon the max value and to the base of 40
      if (minValue <= 0 && minValue >= -1) {
        if (maxValue < 40) minValue = -1
        else minValue = Math.floor((maxValue / 40) * -1)
      }

      options["dataAxis"]["left"] = {
        ...options["dataAxis"]["left"],
        range: { min: minValue, max: maxValue * 1.1 },
      }
    }

    const startOffset = 1000 * startResolutionS
    const endOffset = 1000 * endResolutionS
    if (!endDate || endDate.utcYear() === 1970) endDate = nowDateTime()
    if (lineDate) endDate = lineDate
    options["end"] = toUserTZ(dateFromTimestamp(endDate.unixUtcMillis() + endOffset)).toIsoString()
    options["start"] = toUserTZ(dateFromTimestamp(endDate.unixUtcMillis() - startOffset)).toIsoString()
    return options
  }

  const mapDataPoints = () => {
    let endDate: DateTime | undefined = undefined
    let maxValue: number | undefined = undefined
    let minValue: number | undefined = undefined

    const lGraphGroups = new DataSet<DataGroup>([
      ...groups.map((group, index) => {
        let groupConfig = {
          id: index.toString(),
          content: group.name,
          className: `group${index}-graph2d-style`,
          options: {},
        }

        if (index == 0) {
          groupConfig["options"] = {
            excludeFromLegend: true,
          }
        }

        if (index == 1) {
          groupConfig["options"] = {
            shaded: {
              orientation: "group",
              groupId: (index - 1).toString(),
            },
          }
        }

        if (index == 2) {
          groupConfig["options"] = {
            shaded: {
              enabled: false,
            },
          }
        }

        return groupConfig
      }),
    ])

    let totalMissingGroups = 0
    const missingGroupOffset = 2
    const dataPoints = new DataSet(
      groups
        .map((group, index) => {
          let averageIntervalMS = intervalMS
          if (averageIntervalMS && group.points.length > 1) {
            // Get difference between first and second point (three max)
            const numMax = 3
            for (let i = 0; i < numMax; i++) {
              if (i + 1 >= group.points.length) break
              const point = group.points[i]
              const nextPoint = group.points[i + 1]
              const interval = nextPoint.time.unixUtcMillis() - point.time.unixUtcMillis()
              if (interval > averageIntervalMS) {
                averageIntervalMS = interval
              }
            }
          }

          type zeroPointType = [graph2d.TimePoint, string | undefined, boolean, number, number]
          const zeroPoints: [zeroPointType, zeroPointType][] = []
          let prevPoint: zeroPointType | undefined = undefined
          const valuePoints = group.points.map((point, pointIndex) => {
            const time = point.time
            if (!endDate || time > endDate) {
              endDate = time
            }
            maxValue = maxValue === undefined ? point.value : point.value > maxValue ? point.value : maxValue
            minValue = minValue === undefined ? point.value : point.value < minValue ? point.value : minValue

            if ([0, 1].includes(index) && averageIntervalMS !== undefined) {
              const gapThreshold = averageIntervalMS + Math.min(120000, averageIntervalMS * 2.5)
              if (
                prevPoint !== undefined &&
                time.unixUtcMillis() - prevPoint[0].time.unixUtcMillis() > gapThreshold
              ) {
                zeroPoints.push([prevPoint, [point, graphId, false, pointIndex, index]])
              }
              prevPoint = [point, graphId, true, pointIndex, index]
            }

            return mapPoint(point, index, graphId)
          })

          zeroPoints.forEach((points) => {
            totalMissingGroups += 1
            const groupIndex = (missingGroupOffset + totalMissingGroups) * -1
            points.forEach((point) => {
              const zeroIndex = -1 - point[4]

              if (!lGraphGroups.getIds().includes(`${groupIndex}`))
                lGraphGroups.add({
                  id: `${groupIndex}`,
                  content: `zero`,
                  className: `group${zeroIndex}-graph2d-style`,
                  options: { excludeFromLegend: true },
                })

              // Either add (prev point) or minus depending on if it's a previous point
              point[0].time = point[0].time.sub(seconds(point[2] ? -60 : 60))

              // If previous point add after point if latest point add before
              const pointIndex = point[3] ? point[3] : point[3] - 1
              if (!prevPoint) valuePoints.splice(pointIndex, 1)
              valuePoints.splice(pointIndex, 0, mapPoint(point[0], point[4], point[1]))
              // Add second point with zero value group
              valuePoints.splice(pointIndex - 1, 0, mapPoint(point[0], groupIndex, point[1]))
            })
          })

          if (zeroPoints.length > 0) {
            minValue = -1
          }
          return valuePoints
        })
        .flat() as any as DataItemExtended[]
    )
    setOptions(getOptions(minValue, maxValue, endDate))
    setGraphGroups(lGraphGroups)
    return dataPoints
  }

  useEffect(() => {
    setItems(mapDataPoints())
  }, [groups])

  useEffect(() => {
    let _timeline: Graph2d | null = null

    if (visJsRef.current !== null && items && options && graphGroups) {
      setLoaded(false)
      _timeline = new Graph2d(visJsRef.current, items, graphGroups, options)
      if (lineDate) {
        _timeline.setCurrentTime(normalDate(lineDate))
      }
      if (!fullscreen) {
        _timeline.on("click", (props) => {
          selectGraphPoint(props, _timeline)
        })
      }
      _timeline.on("changed", () => {
        setLoaded(true)
      })
      setTimeline(_timeline)
    }

    return () => {
      _timeline && _timeline.destroy()
      setTimeline(null)
    }
  }, [visJsRef, items, options, graphGroups]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectGraphPoint = (props: TimelineEventPropertiesResult, _timeline: Graph2d | null) => {
    if (items) {
      const clickTime: Date = props.time
      let groups: Record<number, DataItemExtended> = {}

      items.forEach((item) => {
        const extended = item as DataItemExtended
        const groupId = parseInt(extended.group)

        if (groupId < 0) return

        let time = groupId in groups ? groups[groupId] : undefined
        const clickTimestamp = new Date(clickTime).getTime()
        const itemTimestamp = new Date(extended.x).getTime()
        // Closest match to the click timestamp wins
        if (
          time === undefined ||
          extended.x === undefined ||
          itemTimestamp < clickTimestamp ||
          itemTimestamp - clickTimestamp < clickTimestamp - new Date(time.x).getTime()
        ) {
          time = extended
        }

        groups[parseInt(extended.group)] = time
      })

      const mapDataItem = (item: DataItemExtended): DataPoint => {
        return {
          pageX: props.pageX,
          pageY: props.pageY,
          time: typeof item.x === "string" ? new DateTime(item.x as string) : dateFromNormalDate(item.x),
          value: item.y,
          group: item.group,
        }
      }
      const dataPoints: DataPoint[] = Object.entries(groups).map((value) => mapDataItem(value[1]))

      if (_timeline && dataPoints.length > 0) {
        _timeline.setCurrentTime(normalDate(dataPoints[0].time))
      }
      onDataPoint && onDataPoint(dataPoints)
    }
  }

  useEffect(() => {
    if (loaded) {
      onLoaded && onLoaded()
    }
  }, [loaded])

  return (
    <>
      <Box m={1}>
        {!fullscreen && <p>{valueAxis.title}</p>}
        {items && options && graphGroups && (
          <div
            ref={visJsRef}
            onMouseMove={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              e.preventDefault()
              if (fullscreen && timeline) {
                const event = timeline.getEventProperties(e.nativeEvent)
                if (event) selectGraphPoint(event, timeline)
              }
            }}
            onMouseLeave={onMouseLeave}
          />
        )}
      </Box>
    </>
  )
}

export default TimeSeries
